import React, { useState } from 'react';
import { useImpersonation } from '../contexts/ImpersonationContext';

const ImpersonationBanner: React.FC = () => {
  const { isImpersonating, impersonationSession, stopImpersonation, isLoading } = useImpersonation();
  const [isExiting, setIsExiting] = useState(false);

  if (!isImpersonating || !impersonationSession) {
    return null;
  }

  const handleStopImpersonation = async () => {
    if (window.confirm('Are you sure you want to exit impersonation mode?')) {
      setIsExiting(true);
      try {
        await stopImpersonation();
      } catch (error) {
        setIsExiting(false);
        alert('Failed to exit impersonation mode. Please try again.');
      }
    }
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Warning Icon */}
            <div className="flex-shrink-0">
              <svg
                className="w-6 h-6 animate-pulse"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.35 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>

            {/* Message */}
            <div className="flex flex-col">
              <div className="font-bold text-lg">
                🔐 IMPERSONATION MODE ACTIVE
              </div>
              <div className="text-sm opacity-90">
                Viewing as: <span className="font-semibold">
                  {impersonationSession.targetUser.firstName} {impersonationSession.targetUser.lastName}
                </span> ({impersonationSession.targetUser.email})
                {impersonationSession.targetUser.company && (
                  <span className="ml-2">
                    • Company: <span className="font-semibold">{impersonationSession.targetUser.company.name}</span>
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Exit Button */}
          <div className="flex items-center space-x-3">
            <div className="text-sm text-right hidden sm:block">
              <div className="opacity-75">Session expires in</div>
              <div className="font-semibold">{impersonationSession.expiresIn}</div>
            </div>
            <button
              onClick={handleStopImpersonation}
              disabled={isLoading || isExiting}
              className="px-4 py-2 bg-white text-red-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
            >
              {isExiting ? (
                <div className="flex items-center space-x-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Exiting...</span>
                </div>
              ) : (
                'Exit Impersonation'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile-friendly session info */}
      <div className="sm:hidden bg-black bg-opacity-20 px-4 py-2 text-xs text-center">
        Session expires in {impersonationSession.expiresIn}
      </div>
    </div>
  );
};

export default ImpersonationBanner;
